/**
 * Validation Utilities
 * 
 * Cell value validation with type-specific rules
 */

import { TreeGridColumn, TreeGridRow } from '../types';

/**
 * Validation result
 */
export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

/**
 * Validate cell value against column configuration
 */
export function validateCellValue(
  value: any,
  column: TreeGridColumn,
  row: TreeGridRow
): ValidationResult {
  // Check required
  if (column.editable && value === null || value === undefined || value === '') {
    // Allow empty for non-required fields
    return { isValid: true };
  }
  
  // Type-specific validation
  switch (column.editorType) {
    case 'number':
      return validateNumber(value, column);
    case 'text':
      return validateText(value, column);
    case 'date':
      return validateDate(value);
    case 'autocomplete':
      return validateAutocomplete(value, column);
    case 'select':
      return validateSelect(value, column);
    default:
      break;
  }
  
  // Custom validation function
  if (column.validate) {
    const error = column.validate(value, row);
    return {
      isValid: error === null,
      error: error || undefined
    };
  }
  
  return { isValid: true };
}

/**
 * Validate number input
 */
function validateNumber(value: any, column: TreeGridColumn): ValidationResult {
  const numValue = parseFloat(value);
  
  if (isNaN(numValue)) {
    return { isValid: false, error: 'Must be a valid number' };
  }
  
  // Check decimal places
  if (column.numberFormat?.decimals !== undefined) {
    const decimals = column.numberFormat.decimals;
    const valueStr = numValue.toString();
    const decimalPart = valueStr.split('.')[1];
    
    if (decimalPart && decimalPart.length > decimals) {
      return {
        isValid: false,
        error: `Maximum ${decimals} decimal places allowed`
      };
    }
  }
  
  return { isValid: true };
}

/**
 * Validate text input
 */
function validateText(value: any, column: TreeGridColumn): ValidationResult {
  const strValue = String(value);
  
  // Add any text-specific validation here
  // For example: min/max length, pattern matching, etc.
  
  return { isValid: true };
}

/**
 * Validate date input
 */
function validateDate(value: any): ValidationResult {
  if (value instanceof Date) {
    return { isValid: !isNaN(value.getTime()) };
  }
  
  const dateValue = new Date(value);
  if (isNaN(dateValue.getTime())) {
    return { isValid: false, error: 'Invalid date format' };
  }
  
  return { isValid: true };
}

/**
 * Validate autocomplete value
 */
function validateAutocomplete(value: any, column: TreeGridColumn): ValidationResult {
  // If allow custom values, any non-empty value is valid
  if (column.autocompleteConfig?.allowCustomValue) {
    return { isValid: true };
  }
  
  // Otherwise, value must be from the list (validated at selection time)
  return { isValid: true };
}

/**
 * Validate select/dropdown value
 */
function validateSelect(value: any, column: TreeGridColumn): ValidationResult {
  if (!column.selectOptions) {
    return { isValid: true };
  }
  
  const validValues = column.selectOptions.map(opt => opt.value);
  if (!validValues.includes(value)) {
    return { isValid: false, error: 'Invalid selection' };
  }
  
  return { isValid: true };
}

/**
 * Format number for display
 */
export function formatNumber(
  value: number,
  format?: TreeGridColumn['numberFormat']
): string {
  if (!format) {
    return value.toFixed(2);
  }
  
  const { decimals = 2, thousandsSeparator = true, prefix = '', suffix = '' } = format;
  
  let formatted = value.toFixed(decimals);
  
  if (thousandsSeparator) {
    const parts = formatted.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    formatted = parts.join('.');
  }
  
  return `${prefix}${formatted}${suffix}`;
}

/**
 * Parse number from formatted string
 */
export function parseNumber(value: string): number {
  // Remove thousands separators and currency symbols
  const cleaned = value.replace(/[^0-9.-]/g, '');
  return parseFloat(cleaned) || 0;
}

/**
 * Validate row data (all cells)
 */
export function validateRow(
  row: TreeGridRow,
  columns: TreeGridColumn[]
): Record<string, string> {
  const errors: Record<string, string> = {};
  
  columns.forEach(column => {
    if (!column.editable) return;
    
    const value = row.data[column.key];
    const result = validateCellValue(value, column, row);
    
    if (!result.isValid && result.error) {
      errors[column.key] = result.error;
    }
  });
  
  return errors;
}

/**
 * Check if row has any validation errors
 */
export function hasValidationErrors(
  rowId: string,
  validationErrors: Map<string, Map<string, string>>
): boolean {
  const rowErrors = validationErrors.get(rowId);
  return rowErrors ? rowErrors.size > 0 : false;
}

/**
 * Get validation error for specific cell
 */
export function getCellError(
  rowId: string,
  columnKey: string,
  validationErrors: Map<string, Map<string, string>>
): string | null {
  const rowErrors = validationErrors.get(rowId);
  return rowErrors?.get(columnKey) || null;
}

/**
 * Set validation error for cell
 */
export function setCellError(
  rowId: string,
  columnKey: string,
  error: string,
  validationErrors: Map<string, Map<string, string>>
): Map<string, Map<string, string>> {
  const newErrors = new Map(validationErrors);
  
  if (!newErrors.has(rowId)) {
    newErrors.set(rowId, new Map());
  }
  
  newErrors.get(rowId)!.set(columnKey, error);
  return newErrors;
}

/**
 * Clear validation error for cell
 */
export function clearCellError(
  rowId: string,
  columnKey: string,
  validationErrors: Map<string, Map<string, string>>
): Map<string, Map<string, string>> {
  const newErrors = new Map(validationErrors);
  const rowErrors = newErrors.get(rowId);
  
  if (rowErrors) {
    rowErrors.delete(columnKey);
    if (rowErrors.size === 0) {
      newErrors.delete(rowId);
    }
  }
  
  return newErrors;
}

/**
 * Clear all validation errors for row
 */
export function clearRowErrors(
  rowId: string,
  validationErrors: Map<string, Map<string, string>>
): Map<string, Map<string, string>> {
  const newErrors = new Map(validationErrors);
  newErrors.delete(rowId);
  return newErrors;
}
